/* Subscriptions — recurring grocery deliveries. Backed by T-303. */

import { api, mockOr } from '../lib/api.js';
import { D } from '../mock-data.js';
import { ApiError } from '../lib/errors.js';
import { subFiresOn } from '../lib/schedule.js';

export async function listSubscriptions() {
  return mockOr(
    () => api.get('/subscriptions'),
    () => D.subscriptions,
  );
}

export async function createSubscription({ branchId, productId, productName, productEm, variantLabel, qty, cadence, customDays, weeklyDay, deliveryWindow, addressId, paymentMode, pricePaise, branchName }) {
  return mockOr(
    () => api.post('/subscriptions', { branchId, productId, variantLabel, qty, cadence, customDays, weeklyDay, deliveryWindow, addressId, paymentMode }, { idempotencyScope: 'subs.create' }),
    () => {
      // Insert into the local mock store so Home + Subs tab see it immediately.
      const sub = {
        id: 'SUB-' + Math.floor(100 + Math.random() * 900),
        branchId, branchName: branchName || '', productId, productEm: productEm || '🛒',
        product: productName || '', variant: variantLabel || '',
        qty: qty || 1, cadence, customDays, weeklyDay,
        window: deliveryWindow, status: 'active', priceP: pricePaise || 0,
        skippedDates: [],
        nextDel: 'Tomorrow',
      };
      D.subscriptions.unshift(sub);
      return sub;
    },
  );
}

/* createRoutine — batch subscribe across N products / N vendors in one
   action. Returns { routineId, subscriptions, failures }. Mock fans out
   to createSubscription() per item; partial failure surfaces per-item.
   Real backend writes N rows under a routine_id in one transaction. */
export async function createRoutine({ items = [], deliveryWindow, addressId, paymentMode } = {}) {
  return mockOr(
    () => api.post('/routines', { items, deliveryWindow, addressId, paymentMode }, { idempotencyScope: 'routines.create' }),
    async () => {
      const routineId = 'RTN-' + Math.floor(1000 + Math.random() * 9000);
      const subscriptions = [];
      const failures = [];
      for (const it of items) {
        try {
          const sub = await createSubscription({
            branchId: it.branchId,
            branchName: it.branchName,
            productId: it.productId,
            productName: it.productName || it.name,
            productEm: it.productEm || it.em,
            variantLabel: it.variantLabel,
            qty: it.qty,
            cadence: it.cadence,
            customDays: it.customDays,
            weeklyDay: it.weeklyDay,
            deliveryWindow: it.deliveryWindow || deliveryWindow,
            addressId,
            paymentMode,
            pricePaise: it.pricePaise,
          });
          subscriptions.push({ ...sub, routineId });
        } catch (e) {
          failures.push({ item: it, message: e?.message || 'Failed to create' });
        }
      }
      return { routineId, subscriptions, failures };
    },
  );
}

export async function pauseSubscription(id, { resumeAt } = {}) {
  return mockOr(
    () => api.post(`/subscriptions/${id}/pause`, { resumeAt }, { idempotencyScope: 'subs.pause:' + id }),
    () => ({ ok: true, status: 'paused' }),
  );
}

export async function resumeSubscription(id) {
  return mockOr(
    () => api.post(`/subscriptions/${id}/resume`, {}, { idempotencyScope: 'subs.resume:' + id }),
    () => ({ ok: true, status: 'active' }),
  );
}

export async function skipNextDelivery(id) {
  return mockOr(
    () => api.post(`/subscriptions/${id}/skip-next`, {}, { idempotencyScope: 'subs.skip:' + id }),
    () => ({ ok: true }),
  );
}

export async function cancelSubscription(id) {
  return mockOr(
    () => api.post(`/subscriptions/${id}/cancel`, {}, { idempotencyScope: 'subs.cancel:' + id }),
    () => ({ ok: true, status: 'cancelled' }),
  );
}

/* ── Day-level skip (Routine calendar) ──────────────────────────
   Marks a single date as skipped on one subscription. Idempotent —
   tapping skip on an already-skipped date is a no-op server-side.
   Mock mutates D.subscriptions[i].skippedDates in place.
   ─────────────────────────────────────────────────────────────── */
export async function skipDay(subscriptionId, isoDate) {
  return mockOr(
    () => api.post(`/subscriptions/${subscriptionId}/skips`, { date: isoDate }, { idempotencyScope: 'subs.skip-day:' + subscriptionId + ':' + isoDate }),
    () => {
      const sub = D.subscriptions.find((s) => s.id === subscriptionId);
      if (!sub) return { ok: false };
      if (!Array.isArray(sub.skippedDates)) sub.skippedDates = [];
      if (!sub.skippedDates.includes(isoDate)) sub.skippedDates.push(isoDate);
      return { ok: true, subscriptionId, date: isoDate, skippedDates: [...sub.skippedDates] };
    },
  );
}

export async function unskipDay(subscriptionId, isoDate) {
  return mockOr(
    () => api.del(`/subscriptions/${subscriptionId}/skips/${isoDate}`),
    () => {
      const sub = D.subscriptions.find((s) => s.id === subscriptionId);
      if (!sub || !Array.isArray(sub.skippedDates)) return { ok: true };
      sub.skippedDates = sub.skippedDates.filter((d) => d !== isoDate);
      return { ok: true, subscriptionId, date: isoDate, skippedDates: [...sub.skippedDates] };
    },
  );
}

/* Vacation mode — apply skips on every active subscription that fires
   on any of the supplied dates. Server returns affected counts so the
   UI can render a clean toast. */
export async function skipDatesAllActive(isoDates) {
  return mockOr(
    () => api.post('/subscriptions/vacation', { dates: isoDates }, { idempotencyScope: 'subs.vacation:' + isoDates.sort().join(',') }),
    () => {
      let affectedSubscriptionCount = 0;
      let affectedDeliveryCount = 0;
      const touched = new Set();
      for (const sub of D.subscriptions) {
        if (sub.status !== 'active') continue;
        if (!Array.isArray(sub.skippedDates)) sub.skippedDates = [];
        for (const iso of isoDates) {
          const d = new Date(iso);
          if (subFiresOn(sub, d) && !sub.skippedDates.includes(iso)) {
            sub.skippedDates.push(iso);
            affectedDeliveryCount += 1;
            touched.add(sub.id);
          }
        }
      }
      affectedSubscriptionCount = touched.size;
      return { ok: true, affectedSubscriptionCount, affectedDeliveryCount };
    },
  );
}
